import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import IncidentDialog from '../../shared/IncidentDialog';
import { apiErrorMessage } from '../../utils/responseHelpers';

/**
 * Edit dialog for /system-register/incidents.
 * Supports software-linked and IT-asset-linked incidents.
 */
export default function EditIncidentDialog({
  open,
  onClose,
  onSuccess,
  row,
  updateItem,
}) {
  const { user } = useAuth();
  const reportingStaff = user?.data?.name?.trim() || '';
  const [saving, setSaving] = useState(false);

  const initialValues = useMemo(() => {
    if (!row) return null;
    const assetRegisterIds = Array.isArray(row.assetRegisterIds)
      ? row.assetRegisterIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
      : [];
    const hasAssets = assetRegisterIds.length > 0;
    const hasSoftware = row.softwareId != null;
    return {
      ...row,
      id: row.id ?? null,
      softwareId: row.softwareId ?? null,
      targetType: hasSoftware ? 'SOFTWARE' : hasAssets ? 'IT_ASSET' : 'SOFTWARE',
      assetRegisterIds,
      assets: Array.isArray(row.assets) ? row.assets : [],
      incidentTicket: row.incidentTicket ?? '',
      incidentDate: row.incidentDate ?? '',
      incidentDetails: row.incidentDetails ?? '',
      actionTaken: row.actionTaken ?? '',
      rootCause: row.rootCause ?? '',
      reportedBy: row.reportedBy ?? row.incidentReportingStaff ?? '',
      incidentReportingStaff: row.incidentReportingStaff ?? row.reportedBy ?? '',
      riskRegisterIds: Array.isArray(row.riskRegisterIds)
        ? row.riskRegisterIds
        : row.riskRegisterId != null
          ? [row.riskRegisterId]
          : [],
      risks: Array.isArray(row.risks) ? row.risks : [],
      riskPriority: row.riskPriority ?? row.severityLevel ?? row.riskRating ?? '',
      severityLevel: row.severityLevel ?? row.riskPriority ?? row.riskRating ?? '',
      resolutionStatus: row.resolutionStatus ?? row.status ?? 'OPEN',
      incidentResponseOwner: row.incidentResponseOwner ?? '',
      knownIncidents: Boolean(row.knownIncidents ?? true),
      escalationPathDefined: Boolean(row.escalationPathDefined),
      approvals: Array.isArray(row.approvals) ? row.approvals : [],
    };
  }, [row, reportingStaff]);

  const handleSave = async (values) => {
    if (!row?.id) {
      toast.error('Incident record is incomplete.');
      return;
    }
    const isAsset = values?.targetType === 'IT_ASSET'
      || (!values?.softwareId && (values?.assetRegisterIds || []).length > 0);
    const assetIds = isAsset
      ? (values.assetRegisterIds || row.assetRegisterIds || [])
      : [];
    const softwareId = isAsset ? null : (values.softwareId ?? row.softwareId ?? null);
    if (isAsset && !assetIds.length) {
      toast.error('Select at least one assigned device.');
      return;
    }
    if (!isAsset && softwareId == null) {
      toast.error('Select a software system to link this incident.');
      return;
    }
    setSaving(true);
    try {
      await updateItem(row, {
        ...values,
        targetType: isAsset ? 'IT_ASSET' : 'SOFTWARE',
        softwareId,
        assetRegisterIds: isAsset ? assetIds : (values.assetRegisterIds || row.assetRegisterIds || []),
        incidentDetails: values.incidentDetails ?? row.incidentDetails,
        reportedBy: values.reportedBy || row.reportedBy,
        incidentReportingStaff: values.incidentReportingStaff || row.incidentReportingStaff,
        incidentTicket: values.incidentTicket || row.incidentTicket,
        resolutionStatus: values.resolutionStatus || 'OPEN',
      });
      toast.success('Incident updated');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to update incident.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  if (!row || !initialValues) return null;

  return (
    <IncidentDialog
      open={open}
      mode="edit"
      selectTargetType
      initialValues={initialValues}
      defaultReportingStaff={reportingStaff}
      saving={saving}
      onClose={() => {
        if (saving) return;
        onClose?.();
      }}
      onSave={handleSave}
    />
  );
}
